/**
 * ClusteredMap Component
 *
 * A map component that displays multiple report locations with marker clustering.
 * Uses Leaflet.markercluster to group nearby markers based on zoom level.
 */

import React, {useEffect, useMemo, useRef, useState} from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import {Box, IconButton, Paper, Typography} from '@mui/material';
import {Fullscreen, FullscreenExit} from '@mui/icons-material';
import {IrpReportLocationDTO} from '../../../../../../@types/integrated-risk-picture';

// Fix for default markers
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix default icon issue with webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

interface ClusteredMapProps {
    height?: number;
    width?: number;
    defaultZoom?: number;
    /** Report location data from the BFF overview/report-locations endpoint. */
    reports?: IrpReportLocationDTO[];
}

const ClusteredMap: React.FC<ClusteredMapProps> = ({
    height: _height = 250,
    width: _width,
    defaultZoom = 3,
    reports
}) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<L.Map | null>(null);
    const markerClusterGroupRef = useRef<L.MarkerClusterGroup | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const filteredReports = Array.isArray(reports) ? reports : [];

    // Filter reports that have valid latitude and longitude (excluding 0,0 as missing coordinates)
    const reportsWithLocation = useMemo(() =>
        filteredReports.filter(
            report => report.latitude != null &&
                      report.longitude != null &&
                      !(report.latitude === 0 && report.longitude === 0)
        ),
    [filteredReports]);

    // Initialize map once
    useEffect(() => {
        if (!mapRef.current || mapInstanceRef.current) return;

        // Create map with default center (USA)
        const map = L.map(mapRef.current, {
            center: [39.8283, -98.5795], // Center of USA
            zoom: defaultZoom,
            zoomControl: true,
            attributionControl: false,
            dragging: true,
            doubleClickZoom: true,
            scrollWheelZoom: true,
            boxZoom: true,
            keyboard: true,
            touchZoom: true
        });

        // Add tile layer
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
        }).addTo(map);

        // Create marker cluster group
        const markers = L.markerClusterGroup({
            chunkedLoading: true,
            singleMarkerMode: true,
            animateAddingMarkers: false,
            spiderfyOnMaxZoom: true,
            showCoverageOnHover: false,
            zoomToBoundsOnClick: true,
            maxClusterRadius: 80
        });

        markerClusterGroupRef.current = markers;
        map.addLayer(markers);
        mapInstanceRef.current = map;

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
                markerClusterGroupRef.current = null;
            }
        };
    }, []); // Only run once on mount

    // Update markers when filtered reports change
    useEffect(() => {
        if (!mapInstanceRef.current || !markerClusterGroupRef.current) return;

        const markers = markerClusterGroupRef.current;

        // Clear existing markers
        markers.clearLayers();

        // Add new markers for reports with locations.
        // Build the full array first, then call addLayers() once so chunkedLoading
        // can batch the clustering work instead of re-clustering on every addLayer() call.
        if (reportsWithLocation.length > 0) {
            const allMarkers: L.Marker[] = [];
            reportsWithLocation.forEach(report => {
                const { latitude, longitude, reportId, dateTime } = report;
                if (latitude != null && longitude != null) {
                    const marker = L.marker([latitude, longitude], { title: `Report ${reportId}` });
                    marker.bindPopup(`
                        <div style="min-width: 150px;">
                            <strong>Report ${reportId}</strong><br/>
                            <strong>Date:</strong> ${new Date(dateTime as string).toLocaleDateString()}
                        </div>
                    `);
                    allMarkers.push(marker);
                }
            });
            markers.addLayers(allMarkers);
        }
    }, [reportsWithLocation]);

    const handleFullscreen = (): void => {
        if (!mapContainerRef.current) return;

        if (!isFullscreen) {
            // Enter fullscreen
            if (mapContainerRef.current.requestFullscreen) {
                mapContainerRef.current.requestFullscreen();
            } else if ((mapContainerRef.current as any).webkitRequestFullscreen) {
                (mapContainerRef.current as any).webkitRequestFullscreen();
            } else if ((mapContainerRef.current as any).msRequestFullscreen) {
                (mapContainerRef.current as any).msRequestFullscreen();
            }
            setIsFullscreen(true);
        } else {
            // Exit fullscreen
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if ((document as any).webkitExitFullscreen) {
                (document as any).webkitExitFullscreen();
            } else if ((document as any).msExitFullscreen) {
                (document as any).msExitFullscreen();
            }
            setIsFullscreen(false);
        }

        // Force map to recalculate its size after fullscreen change
        setTimeout(() => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.invalidateSize();
            }
        }, 300);
    };

    // Listen for fullscreen changes
    useEffect(() => {
        const handleFullscreenChange = (): void => {
            setIsFullscreen(!!document.fullscreenElement);
            // Force map to recalculate its size
            if (mapInstanceRef.current) {
                setTimeout(() => {
                    mapInstanceRef.current?.invalidateSize();
                }, 100);
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, []);

    return (
        <Paper
            elevation={2}
            sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    p: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: 'background.default'
                }}
            >
                <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                    Report Locations ({reportsWithLocation.length}/{filteredReports.length})
                </Typography>
            </Box>

            {/* Map Container */}
            <Box
                ref={mapContainerRef}
                sx={{
                    position: 'relative',
                    flex: 1,
                    minHeight: 0
                }}
            >
                <div
                    ref={mapRef}
                    style={{
                        height: '100%',
                        width: '100%'
                    }}
                />
                <IconButton
                    size="small"
                    onClick={handleFullscreen}
                    sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        zIndex: 1000,
                        backgroundColor: 'white',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                        '&:hover': {
                            backgroundColor: '#f4f4f4'
                        }
                    }}
                >
                    {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                </IconButton>
            </Box>
        </Paper>
    );
};

export default ClusteredMap;
